import { CommonModule } from "@angular/common";
import {
  AfterViewChecked,
  AfterViewInit,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ViewChild,
} from "@angular/core";
import { FormsModule } from "@angular/forms";
import { MatTooltipModule } from "@angular/material/tooltip";

export type AiDecalChatRole = "user" | "assistant" | "error" | "note" | "question";

export interface AiDecalChatMessage {
  role: AiDecalChatRole;
  text: string;
  // Short backend-issued element code (e.g. "a1"), rendered separately in a
  // monospace font so it reads unambiguously from the surrounding text.
  reference?: string;
  // role: "question" only - 0 to ~4 concrete choices rendered as clickable chips. Empty/undefined
  // means the answer is open-ended free text, so no chips are rendered.
  options?: string[];
}

// Shown one at a time in the prompt placeholder once a turn has completed, cycling to the next
// entry each time isGenerating goes true -> false (see completedTurns).
const SUGGESTED_EDITS: readonly string[] = [
  'Example: "make the blue lighter"',
  'Example: "center the logo on the panel"',
  'Example: "add a black border around the image"',
  'Example: "make the logo bigger"',
  'Example: "add my logo to the top panel"',
];

@Component({
  selector: "app-ai-design-chat",
  standalone: true,
  templateUrl: "./ai-design-chat.component.html",
  styleUrls: ["./ai-design-chat.component.scss"],
  imports: [CommonModule, FormsModule, MatTooltipModule],
})
export class AiDesignChatComponent implements OnChanges, AfterViewInit, AfterViewChecked {
  @ViewChild("transcriptEl") private transcriptEl?: ElementRef<HTMLDivElement>;
  @ViewChild("promptTextarea") private promptTextarea?: ElementRef<HTMLTextAreaElement>;

  @Input() messages: AiDecalChatMessage[] = [];
  @Input() isGenerating = false;
  @Output() promptSubmitted: EventEmitter<string> = new EventEmitter();
  @Output() cleared: EventEmitter<void> = new EventEmitter();

  prompt = "";
  showPromptFocusRing = false;

  private lastRenderedCount = -1;
  private promptMouseDown = false;
  private completedTurns = 0;

  ngOnChanges(changes: SimpleChanges): void {
    const isGeneratingChange = changes["isGenerating"];
    if (isGeneratingChange && isGeneratingChange.previousValue && !isGeneratingChange.currentValue) {
      this.completedTurns++;
      this.focusPrompt();
    }
  }

  ngAfterViewInit(): void {
    this.focusPrompt();
  }

  ngAfterViewChecked(): void {
    if (this.messages.length !== this.lastRenderedCount) {
      this.lastRenderedCount = this.messages.length;
      this.scrollToBottom();
    }
  }

  submit() {
    const trimmed = this.prompt.trim();
    if (!trimmed || this.isGenerating) {
      return;
    }
    this.promptSubmitted.emit(trimmed);
    this.prompt = "";
    const el = this.promptTextarea?.nativeElement;
    if (el) {
      el.style.height = "";
    }
  }

  clear() {
    if (this.isGenerating) {
      return;
    }
    this.cleared.emit();
    this.completedTurns = 0;
    this.prompt = "";
    const el = this.promptTextarea?.nativeElement;
    if (el) {
      el.style.height = "";
    }
  }

  // A clarifying-question option chip - sends immediately as its own turn, same as typing the
  // option's text and hitting submit, rather than prefilling the textarea for editing.
  selectOption(option: string) {
    if (this.isGenerating) {
      return;
    }
    this.promptSubmitted.emit(option);
  }

  onPromptKeydown(event: KeyboardEvent) {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      this.submit();
    }
  }

  onPromptMouseDown() {
    this.promptMouseDown = true;
  }

  onPromptFocus() {
    this.showPromptFocusRing = !this.promptMouseDown;
    this.promptMouseDown = false;
  }

  onPromptBlur() {
    this.showPromptFocusRing = false;
    this.promptMouseDown = false;
  }

  autoGrowTextarea() {
    const el = this.promptTextarea?.nativeElement;
    if (!el) {
      return;
    }
    el.style.height = "auto";
    el.style.height = `${el.scrollHeight}px`;
  }

  get promptPlaceholder() {
    if (this.messages.length <= 1) {
      return 'Example: "make a blue box with white snowflakes"';
    }
    const index = Math.max(0, this.completedTurns - 1) % SUGGESTED_EDITS.length;
    return SUGGESTED_EDITS[index];
  }

  private focusPrompt() {
    setTimeout(() => this.promptTextarea?.nativeElement.focus());
  }

  private scrollToBottom() {
    setTimeout(() => {
      const el = this.transcriptEl?.nativeElement;
      if (el) {
        el.scrollTop = el.scrollHeight;
      }
    });
  }
}
